import type { CSSProperties } from "react"
import { ContainerWidthType } from "@gatsby-plugin-generic-page/fragments/containerSettings"
import {
  PROGRESS_PROPERTY,
  SLIDE_POSITION_PROPERTY,
} from "@gatsby-plugin-generic-page/hooks/useFullVideoSliderScroll"

/**
 * Height of the contained (not yet expanded) video, as a share of the stage.
 * The design's own frames sit at ~0.62; this runs taller so the video fills
 * most of the pinned viewport at rest, leaving only a thin margin rather than
 * a quarter of the screen blank above and below it.
 */
const CONTAINED_HEIGHT_RATIO = 0.9
/** Corner radius of the contained video, in pixels. Flattens out at full bleed. */
const CONTAINED_RADIUS = 16

/**
 * Container widths from `BaseContainer/classNames`, in rem, plus the gutter it
 * applies on both sides. The stage deliberately sits outside `BaseContainer`
 * (it has to reach the screen edges), so the contained size the video starts
 * from is rebuilt here from the same tokens — in CSS, so that the first server
 * render already shows the video at its contained size.
 */
const CONTAINER_MAX_WIDTH_REM: Record<ContainerWidthType, number> = {
  [ContainerWidthType.TINY]: 30,
  [ContainerWidthType.SMALL]: 48,
  [ContainerWidthType.MEDIUM]: 60.5,
  [ContainerWidthType.DEFAULT]: 82,
  [ContainerWidthType.FULL]: 110,
}
/**
 * Half of `BaseContainer`'s 1rem gutter — the contained video is deliberately
 * inset less than regular section content so it starts out wider.
 */
const CONTAINER_GUTTER_REM = 0.5

const progress = `var(${PROGRESS_PROPERTY}, 0)`

/**
 * How far a slide travels vertically between arriving from below and leaving
 * upwards. Lives in a custom property so the reduced-motion media query can zero
 * it out and leave the fades alone. The value is set on the stage — spelled out
 * literally there, because Tailwind scans source text and cannot resolve a class
 * name built from this constant.
 */
const SLIDE_TRAVEL_PROPERTY = "--full-video-slide-travel"
/**
 * How long a slide takes to fade, as a share of one switch. At `0.5` the whole
 * switch is motion: the outgoing slide uses the first half and the incoming one
 * the second, so nothing sits idle mid-animation. The ramps are placed so a
 * slide reaches opacity 0 at exactly the offset where the next starts from 0 —
 * the handover is sequential, never a cross-fade, so two slides are never
 * legible on top of each other. At rest the position is a whole number, which
 * puts one slide at offset 0: centred, full size, fully opaque.
 */
export const SLIDE_FADE_RANGE = 0.5
/** Offset at which a slide starts leaving — where the fade-out ramp begins. */
export const SLIDE_LEAVE_START = 0.5 - SLIDE_FADE_RANGE

/**
 * Scale a slide holds before it has entered, and how much it gains on the way
 * in: `0.8` while below, growing to `1` as it reaches the middle. Only the
 * entering ramp drives it, so a slide leaves at its natural size rather than
 * carrying on past it. Written as two literals rather than deriving the step
 * from the minimum, which lands on values like `0.19999999999999996` and puts
 * them straight into the stylesheet.
 */
const SLIDE_SCALE_MIN = 0.8
const SLIDE_SCALE_STEP = 0.2

/**
 * Size and shape of the video frame at the current expansion, from its
 * contained size out to full bleed. Expressed in CSS against the progress
 * custom property, so the scroll only has to set one number per frame.
 */
export function getFrameStyle(
  containerWidth: ContainerWidthType,
  fallbackImageUrl?: string
): CSSProperties {
  const containedWidth = `min(100% - ${2 * CONTAINER_GUTTER_REM}rem, ${
    CONTAINER_MAX_WIDTH_REM[containerWidth] - 2 * CONTAINER_GUTTER_REM
  }rem)`

  return {
    width: `calc(${containedWidth} + (100% - ${containedWidth}) * ${progress})`,
    height: `calc(${CONTAINED_HEIGHT_RATIO * 100}% + ${(1 - CONTAINED_HEIGHT_RATIO) * 100}% * ${progress})`,
    borderRadius: `calc(${CONTAINED_RADIUS}px * (1 - ${progress}))`,
    // Decorative, so a CSS layer rather than an `<img>` — it also avoids
    // `Picture`'s `height: auto`, which fights a full-bleed cover.
    backgroundImage: fallbackImageUrl
      ? `url("${fallbackImageUrl}")`
      : undefined,
    backgroundSize: "cover",
    backgroundPosition: "center",
  }
}

/**
 * Geometry of one slide, as a function of the shared scroll position. Two ramps
 * drive everything, each spanning `SLIDE_FADE_RANGE` of a switch:
 *
 * - `entered` runs 0 → 1 over offset `-0.5 → SLIDE_LEAVE_START`: the slide
 *   rises from `+travel` into the middle and fades in.
 * - `left` runs 0 → 1 over offset `SLIDE_LEAVE_START → 0.5`: it carries on up
 *   to `-travel` and fades out.
 *
 * At the default half-range the two meet at offset 0, so a slide is only ever
 * still and fully opaque when the position rests on a whole number. The
 * previous slide's `left` ramp finishes at offset `0.5`, which is the same
 * scroll point as the next slide's `entered` ramp starting at its own `-0.5` —
 * one is fully gone before the other appears.
 */
export function getSlideStyle(index: number): CSSProperties {
  const offset = `(var(${SLIDE_POSITION_PROPERTY}, 0) - ${index})`
  const entered = `clamp(0, calc((${offset} + 0.5) / ${SLIDE_FADE_RANGE}), 1)`
  const left = `clamp(0, calc((${offset} - ${SLIDE_LEAVE_START}) / ${SLIDE_FADE_RANGE}), 1)`

  return {
    // Travel: +travel before entering, 0 while held, -travel once left.
    // Scale: 0.8 while still below, growing to 1 as the slide reaches the
    // middle, so it reads as coming towards the reader as it fades in.
    transform: `translateY(calc((1 - ${entered} - ${left}) * var(${SLIDE_TRAVEL_PROPERTY}))) scale(calc(${SLIDE_SCALE_MIN} + ${entered} * ${SLIDE_SCALE_STEP}))`,
    // `min()` of the rising and falling ramps — avoids needing `abs()`, which is
    // still too new to rely on.
    opacity: `min(${entered}, calc(1 - ${left}))`,
  }
}
